import { cx, useTranslations } from "@jugl-web/utils";
import { FC, useMemo } from "react";
import { Avatar, Checkbox } from "@jugl-web/ui-kit";
import { useUserGeneralProfile } from "@jugl-web/domain-resources/users/hooks/useUserGeneralProfile";
import { ChatMessageAttachmentType } from "@jugl-web/rest-api";
import { useSelector } from "react-redux";
import { selectUserId } from "@web-src/modules/auth/store/authSlice";
import { isTextEmojiOnly } from "@jugl-web/utils/isTextEmojiOnly";
import { MediaPreviewAttachment } from "./components/MediaPreviewAttachment";
import { ChatMessageBody } from "./components/ChatMessageBody";
import { ChatMessageFooter } from "./components/ChatMessageFooter";
import { useChatMessageBubble } from "./providers/ChatMessageBubbleProvider/useChatMessageBubble";
import { FileAttachment } from "./components/FileAttachment";
import { DirectGifAttachment } from "./components/DirectGifAttachment";
import { AudioAttachment } from "./components/AudioAttachment";
import { LinkPreview } from "./components/LinkPreview";
import { ReplyMessagePreview } from "./components/ReplyMessagePreview";
import { ContextMenu } from "./components/ContextMenu";
import { Reactions } from "./components/Reactions";
import { ReactComponent as ForwardedIncomingIcon } from "./assets/forwarded-icon-incoming.svg";
import { ReactComponent as ForwardedOutgoingIcon } from "./assets/forwarded-icon.svg";
import { ReactComponent as RetrySendIcon } from "./assets/retry-send.svg";
import { ChatMessageBubbleContextValue } from "./providers/ChatMessageBubbleProvider/ChatMessageBubbleContext";
import { ChatMessageBubbleProvider } from "./providers/ChatMessageBubbleProvider/ChatMessageBubbleProvider";

const CommonMessageBubbleContent: FC = () => {
  const {
    messageFrom,
    messageClientId,
    isOutgoing,
    isGroupChat,
    entityId,
    previewUrl,
    readOnly,
    isSelectable,
    onSelect,
    isSelected,
    isMessageActionsDisabled,
    groupInfo,
    messageTextBody,
    attachments,
    reactions,
    isDeleted,
    deletedBy,
    isUnsupported,
    replyInfo,
    isForwarded,
    senderName,
    senderImage,
    senderAvatarName,
    showOutgoingSenderDetails,
    isError,
    isPending,
    isNotSent,
    onReplyPreviewClick,
    onAvatarClick,
    isHighlighted,
    isPreview,
    isMediaMessageOnly,
    onRequestResend,
  } = useChatMessageBubble();

  const { t } = useTranslations();
  const meId = useSelector(selectUserId);

  const shouldShowSenderDetails =
    (!isOutgoing || showOutgoingSenderDetails) && isGroupChat;
  const shouldShowAvatar = shouldShowSenderDetails && !isPreview;
  const shouldShowUsername =
    shouldShowSenderDetails && (!groupInfo || groupInfo.isFirst);

  const isEmojiOnly = useMemo(
    () => !!messageTextBody && isTextEmojiOnly(messageTextBody),
    [messageTextBody]
  );

  // `isError` (chats: local send failure, retryable) and `isNotSent` (CRM/SMS:
  // provider never sent it, read-only) share the red bubble, but not the click
  // affordance and not the white text-block body treatment
  const hasFailedAppearance = (!!isError && !isPending) || !!isNotSent;

  const { profile: userProfile, safeProfile: userSafeProfile } =
    useUserGeneralProfile({
      entityId,
      userId: messageFrom,
      skip: !isGroupChat || !!senderName,
    });

  const username =
    senderName || userProfile?.displayName || userSafeProfile.displayName;

  const $attachment = useMemo(() => {
    if (!attachments?.length) {
      return null;
    }

    const mediaTypes = [
      ChatMessageAttachmentType.image,
      ChatMessageAttachmentType.video,
    ];

    const areAllAttachmentsSameType =
      attachments.every(
        (attachment) => attachment.type === attachments[0].type
      ) ||
      attachments.every((attachment) => mediaTypes.includes(attachment.type));

    if (!areAllAttachmentsSameType) {
      return <FileAttachment />;
    }

    switch (attachments[0].type) {
      case ChatMessageAttachmentType.image:
      case ChatMessageAttachmentType.gif:
      case ChatMessageAttachmentType.video:
        return <MediaPreviewAttachment />;
      case ChatMessageAttachmentType.gifDirectUrl:
        return <DirectGifAttachment />;
      case ChatMessageAttachmentType.otherFile:
      case ChatMessageAttachmentType.doc:
        return <FileAttachment />;
      case ChatMessageAttachmentType.voice:
      case ChatMessageAttachmentType.audio:
        return <AudioAttachment />;
      default:
        return (
          <div className="flex h-[80px] max-w-[300px] items-center justify-center rounded bg-gray-50 px-8 text-center text-sm text-gray-500">
            {/* TODO: i18n */}
            Attachment type temporarily not supported
          </div>
        );
    }
  }, [attachments]);

  return (
    <div
      className={cx(
        "group relative flex px-[56px] py-1 transition-colors max-sm:px-3",
        {
          "justify-end": isOutgoing,
          "cursor-pointer hover:bg-gray-50": isSelectable,
          "bg-primary-50 hover:bg-primary-50": isHighlighted || isSelected,
          "pb-6": !!reactions?.summary?.length && !readOnly,
        }
      )}
    >
      {isSelectable && (
        <div
          className="absolute top-0 right-0 bottom-0 left-0 z-10"
          onClick={(e) => {
            if (!messageClientId) {
              return;
            }
            e.stopPropagation();
            onSelect?.(messageClientId, !isSelected);
          }}
        />
      )}
      {isSelectable && (
        <div className={cx(isOutgoing ? "mr-auto" : "mr-4")}>
          <div className="flex h-full items-center pr-4">
            <Checkbox
              isChecked={isSelected}
              onClick={(e) => e.stopPropagation()}
              onChange={(e) =>
                messageClientId && onSelect?.(messageClientId, e.target.checked)
              }
            />
          </div>
        </div>
      )}
      <div
        className={cx(
          "flex w-full items-end gap-2",
          isOutgoing && "flex-row-reverse"
        )}
      >
        {shouldShowAvatar && (
          <Avatar
            imageUrl={senderImage || userProfile?.avatar}
            username={senderAvatarName || username}
            size="sm"
            className={cx("block outline outline-1 outline-white", {
              invisible: groupInfo && !groupInfo.isLast,
            })}
            onClick={
              userProfile && onAvatarClick
                ? () => onAvatarClick(userProfile.id)
                : undefined
            }
          />
        )}
        <div
          className={cx(
            "relative max-w-[500px] min-w-[100px] rounded-t-lg px-1 pt-1 pb-[6px]",
            {
              "min-w-[108px]":
                reactions?.summary?.length && reactions?.summary?.length > 3,
              "min-w-[130px]":
                reactions?.summary?.length && reactions?.summary?.length > 4,
              "shadow-[0px 0px 24px 0px rgba(0, 0, 0, 0.05)] rounded-br-lg bg-white":
                !isOutgoing,
              "shadow-[0px 1px 2px 0px rgba(0, 0, 0, 0.17)] rounded-bl-lg bg-[#4E93CA]":
                isOutgoing,
              "rounded-tr-none":
                groupInfo && groupInfo.isLast && !groupInfo.isFirst,
              "rounded-br-none":
                groupInfo && groupInfo.isFirst && !groupInfo.isLast,
              "w-full":
                !isDeleted &&
                !isUnsupported &&
                attachments?.[0]?.type === ChatMessageAttachmentType.voice,
              "bg-gradients-danger": hasFailedAppearance,
              "cursor-pointer": isError && !isPending,
            }
          )}
          onClick={(e) => {
            e.stopPropagation();
            if (!isError || isPending) {
              return;
            }
            onRequestResend?.();
          }}
        >
          {isError && !isPending && isMediaMessageOnly && (
            <div
              className="absolute z-10 flex flex-col items-center justify-center gap-2"
              style={{
                top: "4px",
                bottom: "4px",
                right: "4px",
                left: "4px",
                background: "linear-gradient(135deg, #F4F6F7 0%, #CDE2F2 100%)",
              }}
            >
              <div className="flex h-8 w-8 items-center justify-center rounded-full bg-gradients-danger text-xs">
                <RetrySendIcon />
              </div>
              <div className="font-medium text-tertiary-800">
                {t({
                  id: "chats-page.tap-to-retry",
                  defaultMessage: "Tap to retry",
                })}
              </div>
            </div>
          )}
          {!isSelectable && !isMessageActionsDisabled && <ContextMenu />}
          {isForwarded && !isDeleted && (
            <div className="flex flex-row items-center gap-[2px] font-secondary opacity-70">
              {isOutgoing ? (
                <ForwardedOutgoingIcon />
              ) : (
                <ForwardedIncomingIcon />
              )}
              <span
                className={cx(
                  "text-xs font-medium",
                  isOutgoing ? "text-grey-100" : "text-grey-900"
                )}
              >
                {t({
                  id: "chats-page.forwarded",
                  defaultMessage: "Forwarded",
                })}
              </span>
            </div>
          )}
          {shouldShowUsername && (
            <div
              className={cx(
                "jugl__break-word mb-[2px] px-1 text-[15px] leading-[140%] font-medium",
                isOutgoing ? "text-primary-50" : "text-primary-500"
              )}
            >
              {username}{" "}
              {messageFrom === meId &&
                `(${t({
                  id: "common.you",
                  defaultMessage: "You",
                })})`}
            </div>
          )}
          {isDeleted ? (
            <div
              className={cx(
                "px-1 text-[15px] leading-[130%] text-dark-600 italic",
                { "text-dark-600": !isOutgoing, "text-white": isOutgoing }
              )}
            >
              {deletedBy === "admin"
                ? t({
                    id: "chats-page.message-deleted-by-admin-of-group",
                    defaultMessage:
                      "Message has been deleted by Admin of the Group",
                  })
                : t({
                    id: "chats-page.message-has-been-deleted",
                    defaultMessage: "Message has been deleted",
                  })}
            </div>
          ) : isUnsupported ? (
            <div
              className={cx(
                "px-1 text-[15px] leading-[130%] text-dark-600 italic",
                { "text-dark-600": !isOutgoing, "text-white": isOutgoing }
              )}
            >
              {t({
                id: "chats-page.unsupported-message-format",
                defaultMessage: "Unsupported message format",
              })}
            </div>
          ) : (
            <>
              {replyInfo && (
                <ReplyMessagePreview
                  onClick={
                    !isSelectable && !!onReplyPreviewClick
                      ? () => onReplyPreviewClick(replyInfo)
                      : undefined
                  }
                />
              )}
              {!attachments?.length && !!previewUrl && (
                <LinkPreview url={previewUrl} />
              )}
              {$attachment ? (
                <div
                  className={cx({
                    "relative mb-1": messageTextBody,
                  })}
                >
                  {$attachment}
                </div>
              ) : null}
              {messageTextBody && (
                <div className="px-1 break-words whitespace-pre-wrap">
                  <ChatMessageBody
                    isEmojiOnly={isEmojiOnly}
                    isOutgoing={isOutgoing}
                    body={messageTextBody}
                    // Only the retryable chats failure uses the white
                    // text-block treatment; `isNotSent` keeps plain white text
                    // on the red bubble
                    isFailed={!!isError && !isPending}
                  />
                </div>
              )}
              {!!reactions?.summary?.length && !readOnly && <Reactions />}
            </>
          )}
          <ChatMessageFooter />
        </div>
      </div>
    </div>
  );
};

export const CommonMessageBubble: FC<ChatMessageBubbleContextValue> = (
  props
) => (
  <ChatMessageBubbleProvider {...props}>
    <CommonMessageBubbleContent />
  </ChatMessageBubbleProvider>
);
